<script setup>
import {
    computed, inject, ref, watch,
} from "vue";
import AddNewEventModal from "../../components/AddNewEventModal/AddNewEventModal.vue";
import ButtonComponent from "@/components/Button/ButtonComponent.vue";
import { useMainStore } from "@/stores/mainStore";
import { usePricingStore } from "../../../../stores/pricingStore";
import { useBookingSettingsStore } from "../../../../stores/bookingStore";

const $storage = inject("$storage");
const props = defineProps({
    termData: {
        type: Object,
        default: () => {
        },
    },
    repetition: {
        type: String,
        default: "",
    },
    venueId: {
        type: String,
        default: "",
    },
    selectedDay: {
        type: String,
        default: "",
    },
    holidays: {
        type: Array,
        default: () => [],
    },
});

const emit = defineEmits(["update"]);

const addNewEvent = ref();
const showAddNewEventModal = ref(false);
const addEventToVenueId = ref(null);
const mainStore = useMainStore();
const bookingSettingsStore = useBookingSettingsStore();
const pricingStore = usePricingStore();
const triggerReset = ref(false);
const availableActivity = ref({});
const showSpinner = ref(false);

async function handleSubmitAddNewEventModal(data) {
    showSpinner.value = true;
    await mainStore.addNewEvent(
        data.value,
        props.termData.termId,
        addEventToVenueId.value,
        props.repetition,
    );

    triggerReset.value = true;
    showAddNewEventModal.value = false;
    showSpinner.value = false;
    emit("update", { day: data.value.dates.day, venueId: props.venueId });
    pricingStore.reset($storage);
    bookingSettingsStore.removeLocalStorage($storage);
}

watch(
    () => triggerReset,
    (value) => {
        if (value === true) {
            triggerReset.value = false;
        }
    },
);

function toggleShowAddNewEventModal(venueId) {
    showAddNewEventModal.value = !showAddNewEventModal.value;
    addEventToVenueId.value = venueId;
}

const getActivitiesList = computed(() => {
    const activities = mainStore.allActivities.filter(
        (activity) => (
            activity.flow_type === "advanced"
            && activity.status !== "container"
        ),
    );

    const filteredActivities = activities.map((a) => {
        const ages = Object.values(mainStore.agesForActivities[a.activity_id])
            .map((aa) => aa.map((fa) => fa.name).join(", "))
            .join(", ");

        const currentActivityCategories = mainStore.actCategories
            .filter((ac) => ac.activity_id === a.activity_id);

        const rawPrimaryCategory = currentActivityCategories.find((c) => c.category_type === "0");
        const rawSecondaryCategories = currentActivityCategories
            .filter((c) => c.category_type === "1")
            .map((c) => c.category_id);

        const primaryCategory = mainStore.allCategories.find(
            (ac) => ac.category_id === rawPrimaryCategory?.category_id,
        );

        const secondaryCategory = rawSecondaryCategories.length > 0
            ? mainStore.allCategories.filter(
                (ac) => rawSecondaryCategories.indexOf(ac.category_id) !== -1,
            )
            : [];

        return {
            activityId: a.activity_id,
            img: mainStore.IMAGE_BASE_LINK + a.listing_image_name,
            name: a.title,
            ages,
            description: a.description,
            eventType: a.event_type,
            primaryCategory: primaryCategory?.name || "",
            secondaryCategories: secondaryCategory.map((c) => c.name).join(", "),
            bookingEmail: a.booking_email,
            bookingType: a.booking_required === "1" ? "required" : "drop-in",
        };
    });

    return filteredActivities;
});

const getAvailableActivities = computed(
    () => mainStore.allActivities.filter(
        (activity) => (
            activity.flow_type === "advanced"
        ),
    )
        .map((activity) => {
            const ages = mainStore.agesForActivities[activity.activity_id] !== null
            && mainStore.agesForActivities[activity.activity_id] !== undefined
                ? Object.values(mainStore.agesForActivities[activity.activity_id])
                    .map((aa) => aa.map((a) => a.name).join(", ")).join(", ")
                : "";

            return {
                title: `${activity.title}  ${ages}`,
                activity_id: activity.activity_id,
            };
        }),
);

function handleAvailableActivityChange(data) {
    if (data !== null) {
        const activity = mainStore.allActivities.find((a) => a.activity_id === data.activity_id);

        const currentActivityCategories = mainStore.actCategories.filter(
            (ac) => ac.activity_id === data.activity_id,
        );

        const rawPrimaryCategory = currentActivityCategories.find((c) => c.category_type === "0");
        const rawSecondaryCategories = currentActivityCategories.filter(
            (c) => c.category_type === "1",
        ).map((c) => c.category_id);

        const primaryCategory = mainStore.allCategories.find(
            (ac) => ac.category_id === rawPrimaryCategory?.category_id,
        );
        let secondaryCategory = [];
        if (rawSecondaryCategories.length > 0) {
            secondaryCategory = mainStore.allCategories.find(
                (ac) => rawSecondaryCategories.indexOf(ac.category_id) !== -1,
            );
        }

        availableActivity.value = {
            eventType: activity.event_type,
            name: "",
            ages: [],
            primaryCategory: primaryCategory || null,
            secondaryCategories: secondaryCategory || null,
            img: mainStore.IMAGE_BASE_LINK + activity.listing_image_name,
            description: activity.description,
            bookingEmail: activity.booking_email,
            bookingType: activity.booking_required === "1" ? "required" : "drop-in",
        };
    } else {
        availableActivity.value = {
            eventType: "",
            name: "",
            ages: [],
            primaryCategory: null,
            secondaryCategories: null,
            img: "",
            description: "",
            bookingEmail: "",
            bookingType: "",
        };
    }
}
</script>

<template>
    <ButtonComponent
        @click="toggleShowAddNewEventModal(venueId)"
        dataTest="btn-add-event"
        class="btn-add-event"
        :class="{
            'ml-auto': repetition === 'weekly',
            'ml-0': repetition === 'daily',
        }"
    >
        {{ repetition === "weekly" ? "+ Add an event" : "+ Add daily event" }}
    </ButtonComponent>
    <AddNewEventModal
        ref="addNewEvent"
        v-if="showAddNewEventModal"
        :term="termData"
        :holidays="holidays"
        :show-add-event-modal="showAddNewEventModal"
        :categories="mainStore.allCategories"
        :repetition="repetition"
        :trigger-reset="triggerReset"
        :activities-list="getActivitiesList"
        :available-activities="getAvailableActivities"
        :selected-available-activity="availableActivity"
        :spinner="{
            show: showSpinner,
            color: 'white',
        }"
        :selected-day="selectedDay"
        @close="showAddNewEventModal = false"
        @submit="handleSubmitAddNewEventModal"
        @change-available-activity="handleAvailableActivityChange"
    />
</template>
<style scoped>
button {
    padding: 0.5625rem 1.5rem !important;
}

@media only screen and (max-width: 768px) {
    .btn-add-event {
        width: 100%;
        margin-left: 0 !important;
        margin-right: 0 !important;
    }
}

</style>
